import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { routes } from "@/routes";

/**
 * Hard gate applied around every protected app route.
 *
 * An authenticated user who has NOT accepted the Terms of Service is
 * redirected to `/accept-terms`. This mirrors OrgMembershipGuard and runs
 * on every navigation — including deep-links and hard refreshes — so the
 * gate cannot be bypassed by loading a URL directly.
 *
 * `/accept-terms` itself lives OUTSIDE this guard (see App.tsx) so there is
 * no redirect loop. Auth is enforced one level up by ProtectedRoute, so a
 * logged-out user never reaches this component with a null user for long.
 *
 * While the session is still resolving we render a spinner rather than a
 * redirect, to avoid flashing the ToS page before we know the real state.
 */
export function AcceptTermsGuard() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not authenticated: ProtectedRoute (above) handles the /auth redirect.
  // Render nothing to avoid briefly flashing gated content.
  if (!user) {
    return null;
  }

  // Only redirect on an explicit `false`. If the field is absent (e.g. an
  // older payload) we fail open rather than trapping the user.
  if (user.hasAcceptedTos === false) {
    return <Navigate to={routes.acceptTerms()} replace />;
  }

  return <Outlet />;
}
